import { NgClass } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type CheckBoxState = 'UNCHECKED' | 'CHECKED' | 'INDETERMINATE' | 'DISABLED' | 'DISABLED-CHECKED';

@Component({
  selector: 'ac-checkbox',
  standalone: true,
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CheckboxComponent,
      multi: true,
    },
  ],
})
export class CheckboxComponent implements ControlValueAccessor {
  @Output() changeValue: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() set state(st: CheckBoxState) {
    this.checked = st === 'CHECKED' || st === 'DISABLED-CHECKED';
    this.indeterminate = st === 'INDETERMINATE';
    this.disabled = st === 'DISABLED' || st === 'DISABLED-CHECKED';
    this._onChange(this.checked);
    this._onTouched();
  }

  /**
   * On submit form you'll get a string of name/value pairs:
   * name=id&name=id (society=code1&society=code2)
   */
  @Input() name?: string;
  @Input() id: string = crypto.randomUUID();

  /** Error style */
  @Input() error: boolean = false;

  /** Negative style for dark backgrounds */
  @Input({ transform: booleanAttribute }) negative?: boolean | string;

  indeterminate: boolean = false;
  checked: boolean = false;
  disabled: boolean = false;

  private _onChange: (value: boolean) => void = () => {};
  private _onTouched: () => void = () => {};

  /**
   * Click and Keydown events
   * Changes check value if not disabled and emits its value
   *
   */
  @HostListener('click', ['$event'])
  @HostListener('Keydown.space', ['$event'])
  @HostListener('Keydown.enter', ['$event'])
  protected _changeCheckValue(): void {
    if (!this.disabled) {
      if (this.indeterminate) {
        this.checked = true;
      } else {
        this.checked = !this.checked;
      }
      this._onChange(this.checked);
      this.changeValue.emit(this.checked);
    }
  }

  /** ControlValueAccessor implementation */
  /**
   * Saves a callback function to be invoked when the selects value
   * changes from user input. Part of the ControlValueAccessor interface
   * required to integrate with Angular's core forms API.
   *
   * @param fn Callback to be triggered when the value changes.
   */
  registerOnChange(fn: (value: boolean | null) => void): void {
    this._onChange = fn;
  }

  /**
   * Saves a callback function to be invoked when the select is blurred
   * by the user. Part of the ControlValueAccessor interface required
   * to integrate with Angular's core forms API.
   *
   * @param fn Callback to be triggered when the component has been touched.
   */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  writeValue(checked: boolean): void {
    this.checked = checked;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
